import { useQuery } from '@tanstack/react-query'
import { healthApi } from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Server, Database, HardDrive, Activity, Clock, Cpu } from 'lucide-react'
import type { HealthStatus, ComponentHealth } from '@/types'

// Health check auto-refresh interval from environment variable
// Default: 600000ms (10 minutes), set to 0 to disable auto-refresh
const HEALTH_CHECK_REFRESH_INTERVAL = parseInt(import.meta.env.VITE_HEALTH_CHECK_REFRESH_INTERVAL || '600000', 10)

const statusConfig: Record<HealthStatus, { color: string; icon: React.ReactNode; variant: 'default' | 'secondary' | 'destructive' }> = {
  HEALTHY: { color: 'text-green-600', icon: <CheckCircle2 className="h-5 w-5 text-green-600" />, variant: 'default' },
  DEGRADED: { color: 'text-yellow-600', icon: <AlertTriangle className="h-5 w-5 text-yellow-600" />, variant: 'secondary' },
  UNHEALTHY: { color: 'text-red-600', icon: <XCircle className="h-5 w-5 text-red-600" />, variant: 'destructive' },
}

const componentIcons: Record<string, React.ReactNode> = {
  Database: <Database className="h-5 w-5" />,
  Storage: <HardDrive className="h-5 w-5" />,
  Scheduler: <Clock className="h-5 w-5" />,
}

function ComponentCard({ component }: { component: ComponentHealth }) {
  const config = statusConfig[component.status]
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {componentIcons[component.name] || <Server className="h-5 w-5" />}
            <CardTitle className="text-lg">{component.name}</CardTitle>
          </div>
          {config.icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Status</span>
            <Badge variant={config.variant}>{component.status}</Badge>
          </div>
          {component.message && (
            <p className="text-sm text-muted-foreground">{component.message}</p>
          )}
          {component.response_ms !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Response Time</span>
              <span className="text-sm font-medium">{component.response_ms}ms</span>
            </div>
          )}
          {component.details && Object.entries(component.details).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
              <span className="text-sm font-medium">{value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

export default function HealthCheckPage() {
  const { data: health, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['system-health'],
    queryFn: healthApi.getSystemHealth,
    // Auto-refresh interval configurable via VITE_HEALTH_CHECK_REFRESH_INTERVAL env var
    // Default: 10 minutes, set to 0 to disable
    refetchInterval: HEALTH_CHECK_REFRESH_INTERVAL > 0 ? HEALTH_CHECK_REFRESH_INTERVAL : false,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const overallConfig = health ? statusConfig[health.status] : statusConfig.HEALTHY

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">System Health</h1>
          <p className="text-muted-foreground">Monitor the health and performance of your platform</p>
        </div>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overall Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {overallConfig.icon}
              <div>
                <CardTitle>Overall Status: {health?.status}</CardTitle>
                <CardDescription>
                  Uptime: {health?.uptime} | Version: {health?.version} | Environment: {health?.environment}
                </CardDescription>
              </div>
            </div>
            <span className="text-sm text-muted-foreground">
              Last checked: {health?.checked_at ? new Date(health.checked_at).toLocaleTimeString() : '-'}
            </span>
          </div>
        </CardHeader>
      </Card>

      {/* System Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Cpu className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Memory Usage</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{health?.metrics.memory_used_mb} MB</div>
            <Progress value={health?.metrics.memory_percent || 0} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              {health?.metrics.memory_percent?.toFixed(1)}% of {health?.metrics.memory_total_mb} MB
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Goroutines</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{health?.metrics.goroutine_count}</div>
            <p className="text-xs text-muted-foreground mt-1">Active concurrent processes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">DB Connections</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{health?.metrics.open_connections}</div>
            <p className="text-xs text-muted-foreground mt-1">Open database connections</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Started At</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">
              {health?.started_at ? new Date(health.started_at).toLocaleDateString() : '-'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {health?.started_at ? new Date(health.started_at).toLocaleTimeString() : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Component Health */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Component Health</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {health?.components.map((component) => (
            <ComponentCard key={component.name} component={component} />
          ))}
        </div>
      </div>
    </div>
  )
}

